// services/api.js
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const getHeaders = () => ({
  'Content-Type': 'application/json',
});

// Helper to handle responses
const handleResponse = async (response) => {
  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(errorText || `API error: ${response.status}`);
  }
  return response.json();
};

// ========== Filter Options ==========
export const fetchSimpleOptions = async (userId, filterType, signal) => {
  let endpoint = '';
  let responseField = 'jobs';

  switch (filterType) {
    case 'date':
      endpoint = `${API_BASE_URL}/api/Filters/GetFilteredSiteNoteDate/${userId}`;
      break;
    case 'workspace':
      endpoint = `${API_BASE_URL}/api/Filters/GetFilteredWorkspace/${userId}`;
      break;
    case 'project':
      endpoint = `${API_BASE_URL}/api/Filters/GetFilteredProject/${userId}`;
      break;
    case 'job':
      endpoint = `${API_BASE_URL}/api/Filters/GetFilteredJob/${userId}`;
      break;
    case 'task':
      endpoint = `${API_BASE_URL}/api/Filters/GetFilteredTask/${userId}`;
      responseField = 'tasks';
      break;
    case 'userName':
      endpoint = `${API_BASE_URL}/api/Filters/GetFilteredSiteNoteUser/${userId}`;
      break;
    default:
      return [];
  }

  const response = await fetch(endpoint, { signal });
  const data = await handleResponse(response);
  return data[responseField] || [];
};

export const fetchFilteredOptions = async (userId, filterType, combination, signal) => {
  let endpoint = '';
  let responseField = 'jobs';

  switch (filterType) {
    case 'date':
      endpoint = `${API_BASE_URL}/api/Filters/GetFilteredSiteNoteDate/${userId}`;
      break;
    case 'workspace':
      endpoint = `${API_BASE_URL}/api/Filters/GetFilteredWorkspace/${userId}`;
      break;
    case 'project':
      endpoint = `${API_BASE_URL}/api/Filters/GetFilteredProject/${userId}`;
      break;
    case 'job':
      endpoint = `${API_BASE_URL}/api/Filters/GetFilteredJob/${userId}`;
      break;
    case 'userName':
      endpoint = `${API_BASE_URL}/api/Filters/GetFilteredSiteNoteUser/${userId}`;
      break;
    case 'task':
      endpoint = `${API_BASE_URL}/api/Filters/GetFilteredTask/${userId}`;
      responseField = 'tasks';
      break;
    default:
      return [];
  }

  const queryParams = new URLSearchParams();
  if (combination.workspace) queryParams.append('WorkspaceId', combination.workspace);
  if (combination.project) queryParams.append('ProjectId', combination.project);
  if (combination.job) queryParams.append('JobId', combination.job);
  if (combination.date) queryParams.append('NoteDate', combination.date);
  if (combination.task) queryParams.append('TaskId', combination.task);
  if (combination.userName) queryParams.append('UserId', combination.userName);

  const url = queryParams.toString() ? `${endpoint}?${queryParams.toString()}` : endpoint;
  const response = await fetch(url, { signal });
  const data = await handleResponse(response);
  return data[responseField] || [];
};

// ========== Notes with Filters ==========
export const fetchFilteredNotesForCombination = async (userId, combination, searchText, apiUrl) => {
  const params = new URLSearchParams({
    pageNumber: '1',
    pageSize: '100',
    userId: userId,
  });

  if (combination.date) params.append('date', combination.date);
  if (combination.workspace) params.append('workspaceId', combination.workspace);
  if (combination.project) params.append('projectId', combination.project);
  if (combination.job) params.append('jobId', combination.job);
  if (combination.userName) params.append('siteNoteUserId', combination.userName);
  if (combination.task) params.append('taskId', combination.task);

  let url;
  if (searchText && searchText.trim() !== '') {
    params.append('searchText', searchText);
    url = `${apiUrl}/SiteNote/GetSiteNotesWithTextFilter?${params.toString()}`;
  } else {
    url = `${apiUrl}/SiteNote/GetSiteNotesWithFilters?${params.toString()}`;
  }

  const response = await fetch(url);
  const data = await handleResponse(response);
  const notesList = data.data || data.siteNotes || [];
  return notesList.map(n => ({
    ...n,
    id: n.id,
    userName: n.userName || n.UserName,
    documentCount: n.documentCount ?? n.DocumentCount ?? 0,
    note: n.note || n.content || '',
    timeStamp: n.timeStamp || n.date,
    date: n.date || n.createdDate || new Date().toISOString(),
    workspace: n.workspace || n.workspaceName || '',
    project: n.project || n.projectName || '',
    job: n.job || '',
    jobId: n.jobId || '',
  }));
};

// ========== Stacked Jobs ==========
export const fetchStackedJobsFromApi = async (userId, apiUrl) => {
  const url = `${apiUrl}/SiteNote/GetStackedJobs?userId=${userId}&pageNumber=1&pageSize=50`;
  const response = await fetch(url);
  const data = await handleResponse(response);
  if (!data.jobs || !Array.isArray(data.jobs)) return [];

  return data.jobs.map(job => {
    return {
      jobId: job.jobId || job.id || job.jobID || job.Id || job.JobId || "",
      jobName: job.jobName || job.name || job.job || "",
      jobDescription: job.jobDescription || job.description || job.Name || "",
      projectId: job.projectId || job.project || "",
      projectName: job.projectName || job.projectName || job.project || "",
      workspaceId: job.workspaceId || job.workspace?.id || job.WorkspaceId || "",
      workspaceName: job.workspaceName || job.workspace || "",
      noteCount: job.totalSiteNotes || job.matchingNotesCount || 0,
      latestTimeStamp: job.latestTimeStamp || job.latestMatchingNote,
      lastSiteNoteUserName: job.lastSiteNoteUserName || null,
      lastSiteNote: job.lastSiteNote || "",
      friendlyId:
        job.friendlyId || job.FriendlyId || job.jobFriendlyId || job.jobFriendlyID || job.job?.friendlyId || "",
      isLoadingNotes: false,
      hasLoadedNotes: false,
      notes: null,
      errorLoadingNotes: null,
    };
  });
};

export const fetchStackedJobsWithSearch = async (userId, searchText, apiUrl) => {
  const params = new URLSearchParams({ searchText, userId });
  const url = `${apiUrl}/SiteNote/GetJobsBySiteNoteText?${params.toString()}`;
  const response = await fetch(url);
  const data = await handleResponse(response);
  return (data.jobs || []).map(job => {
    return {
      jobId: job.jobId || job.id || job.jobID || job.Id || job.JobId || "",
      jobName: job.jobName || job.name || job.job || "",
      jobDescription: job.jobDescription || job.description || job.Name || "",
      noteCount: job.matchingNotesCount || job.totalSiteNotes || 0,
      latestTimeStamp: job.latestMatchingNote || job.latestTimeStamp,
      workspaceName: job.workspaceName || job.workspace || "",
      projectName: job.projectName || job.project || "",
      projectId: job.projectId || job.project || "",
      friendlyId:
        job.friendlyId || job.FriendlyId || job.jobFriendlyId || job.jobFriendlyID || job.job?.friendlyId || "",
      isLoadingNotes: false,
      hasLoadedNotes: false,
      notes: null,
      errorLoadingNotes: null,
    };
  });
};

// ========== Tasks and Notes by Task ==========
export const fetchTasksForJobApi = async (jobId, apiUrl) => {
  const response = await fetch(`${apiUrl}/JobTasks/job/${jobId}`);
  const data = await handleResponse(response);
  return data.success && data.data ? data.data : [];
};

export const fetchNotesForTaskApi = async (userId, taskId, page = 1, pageSize = 20, apiUrl) => {
  const params = new URLSearchParams({
    pageNumber: page.toString(),
    pageSize: pageSize.toString(),
    taskId,
    userId,
  });
  const url = `${apiUrl}/SiteNote/GetSiteNotesWithFilters?${params.toString()}`;
  const response = await fetch(url);
  const data = await handleResponse(response);
  const notesArray = data.data || data.siteNotes || [];
  const totalCount = data.totalCount || data.total || data.totalRecords || notesArray.length;
  const hasMore = (page - 1) * pageSize + notesArray.length < totalCount;

  const formattedNotes = notesArray.map(n => ({
    ...n,
    id: n.id,
    userName: n.userName || n.UserName || 'Unknown',
    documentCount: n.documentCount || 0,
    timeStamp: n.timeStamp || n.date,
    date: n.date || n.createdDate,
    workspace: n.workspace || '',
    project: n.project || '',
    job: n.job || '',
    note: n.note || n.content,
    jobId: n.jobId,
    taskId: n.taskId,
  }));

  return { notes: formattedNotes, totalCount, hasMore };
};

// ========== Inline Images ==========
export const fetchInlineImagesApi = async (noteId, apiUrl) => {
  const response = await fetch(`${apiUrl}/InlineImages/GetInlineImagesBySiteNote?siteNoteId=${noteId}`);
  if (!response.ok) return [];
  const data = await response.json();
  return data.images || [];
};

export const deleteInlineImagesApi = async (noteId, apiUrl) => {
  const images = await fetchInlineImagesApi(noteId, apiUrl);
  for (const image of images) {
    await fetch(`${apiUrl}/InlineImages/DeleteInlineImage/${image.id}`, { method: 'DELETE' });
  }
  return true;
};

// ========== Attached Documents ==========
export const fetchAttachedDocumentsApi = async (noteId, apiUrl) => {
  const response = await fetch(`${apiUrl}/Documents/GetDocumentMetadataByReference?siteNoteId=${noteId}`);
  if (!response.ok) return [];
  const data = await response.json();
  return data.documents || [];
};

export const deleteAttachedDocumentsApi = async (noteId, apiUrl) => {
  const docs = await fetchAttachedDocumentsApi(noteId, apiUrl);
  for (const doc of docs) {
    await fetch(`${apiUrl}/Documents/DeleteDocument/${doc.id}`, { method: 'DELETE' });
  }
  return true;
};

// ========== Delete Note ==========
export const deleteNoteApi = async (noteId, userId, apiUrl) => {
  const url = new URL(`${apiUrl}/SiteNote/DeleteSiteNote/${noteId}`);
  url.searchParams.append('userId', userId);
  const response = await fetch(url.toString(), { method: 'DELETE', headers: getHeaders() });
  return handleResponse(response);
};

// ========== User and Workspace ==========
export const fetchCurrentUserApi = async (userId, apiUrl) => {
  const response = await fetch(`${apiUrl}/UserManagement/GetUserById/${userId}`);
  if (!response.ok) return null;
  const data = await response.json();
  return data.user;
};

export const fetchWorkspacesByUserIdApi = async (userId, apiUrl) => {
  const response = await fetch(`${apiUrl}/Workspace/GetActiveWorkspacesNameByUserId/${userId}`);
  if (!response.ok) return [];
  const data = await response.json();
  return data.workspaces || data || [];
};

export const fetchWorkspaceRoleApi = async (userId, workspaceId, apiUrl) => {
  const response = await fetch(`${apiUrl}/Workspace/GetWorkspacesByUserId/${userId}`);
  const data = await handleResponse(response);
  const workspaces = data.workspaces || [];
  const defaultWorkspace = workspaces.find(w => (w.id || w.workspaceId)?.toString() === workspaceId?.toString());
  return defaultWorkspace?.role || null;
};